import { useState, useEffect, useCallback, useRef } from 'react';
import { Joyride, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ArrowRight, BarChart3, CheckSquare, AlertTriangle,
  Sparkles, Video, Zap, ChevronLeft, ChevronRight, Rocket,
  Volume2, VolumeX
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOUR_KEY = 'd3_product_tour';
const TOUR_AUTO_KEY = 'd3_tour_auto_triggered';
const TOUR_MUTE_KEY = 'd3_tour_muted';

// ── Audio Manager ───────────────────────────────────────
const useTourAudio = () => {
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(() => localStorage.getItem(TOUR_MUTE_KEY) === 'true');
  const [audioReady, setAudioReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  // Check if audio files exist on mount
  useEffect(() => {
    fetch(`${API_URL}/api/tour/audio-status`)
      .then(r => r.json())
      .then(d => setAudioReady(d.ready === true))
      .catch(() => setAudioReady(false));
  }, []);

  const play = useCallback((key) => {
    if (!audioReady || muted) return;
    // Stop current audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    const audio = new Audio(`${API_URL}/api/tour/audio/${key}`);
    audio.volume = 0.85;
    audio.onplay = () => setPlaying(true);
    audio.onended = () => setPlaying(false);
    audio.onpause = () => setPlaying(false);
    audio.onerror = () => setPlaying(false);
    audioRef.current = audio;
    audio.play().catch(() => {});
  }, [audioReady, muted]);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      localStorage.setItem(TOUR_MUTE_KEY, String(next));
      if (next && audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setPlaying(false);
      }
      return next;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  return { play, stop, toggleMute, muted, audioReady, playing };
};

// ── Tour Step Definitions ───────────────────────────────
const TOUR_STEPS = [
  {
    target: '.tour-kpi-grid',
    content: 'Get real-time visibility into your operations — tasks, risks, and performance in one place.',
    placement: 'bottom',
    data: { icon: BarChart3, label: 'Command Center', title: 'Real-time visibility into your operations', body: 'Tasks, risks, meetings, and performance — everything your leadership team needs to make decisions, updated in real-time.', accent: 'indigo' },
  },
  {
    target: '.tour-kpi-overdue-tasks',
    content: 'Track execution across teams and instantly identify what needs attention.',
    placement: 'bottom',
    data: { icon: CheckSquare, label: 'Execution Tracking', title: 'Track execution across every team', body: 'Instantly identify overdue work, blocked tasks, and who needs support — before deadlines slip.', accent: 'orange' },
  },
  {
    target: '.tour-kpi-active-risks',
    content: 'Surface and prioritize risks before they impact your business.',
    placement: 'bottom',
    data: { icon: AlertTriangle, label: 'Risk Intelligence', title: 'Surface risks before they become problems', body: 'AI flags high-severity risks across projects and recommends mitigation strategies automatically.', accent: 'red', pulse: true },
  },
  {
    target: '.tour-ai-insights',
    content: 'AI continuously analyzes activity and flags issues like declining productivity.',
    placement: 'left',
    data: { icon: Sparkles, label: 'Core Differentiator', title: 'AI that works while you sleep', body: 'Continuously analyzes activity across meetings, tasks, and risks — then surfaces insights like declining productivity or emerging blockers.', accent: 'cyan' },
  },
  {
    target: '.tour-kpi-meetings-processed',
    content: 'Turn meetings into trackable actions — nothing falls through the cracks.',
    placement: 'bottom',
    data: { icon: Video, label: 'Meeting Intelligence', title: 'Turn meetings into trackable actions', body: 'Every meeting is automatically transcribed, analyzed, and converted into tasks, risks, and decisions. Nothing falls through the cracks.', accent: 'green' },
  },
  {
    target: '.tour-action-create-task',
    content: 'Capture work instantly and keep teams accountable.',
    placement: 'top',
    data: { icon: Zap, label: 'Instant Capture', title: 'Capture work and keep teams accountable', body: 'Create tasks, log risks, or upload meetings in one click. Every action is tracked, assigned, and visible to leadership.', accent: 'purple' },
  },
];

const ACCENT_MAP = {
  indigo: { bg: 'from-indigo-500/20 to-indigo-600/10', border: 'border-indigo-500/30', text: 'text-indigo-400', glow: 'shadow-indigo-500/20' },
  orange: { bg: 'from-orange-500/20 to-orange-600/10', border: 'border-orange-500/30', text: 'text-orange-400', glow: 'shadow-orange-500/20' },
  red:    { bg: 'from-red-500/20 to-red-600/10', border: 'border-red-500/30', text: 'text-red-400', glow: 'shadow-red-500/20' },
  cyan:   { bg: 'from-cyan-500/20 to-cyan-600/10', border: 'border-cyan-500/30', text: 'text-cyan-400', glow: 'shadow-cyan-500/20' },
  green:  { bg: 'from-green-500/20 to-green-600/10', border: 'border-green-500/30', text: 'text-green-400', glow: 'shadow-green-500/20' },
  purple: { bg: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-500/30', text: 'text-purple-400', glow: 'shadow-purple-500/20' },
};

// ── Custom Tooltip ──────────────────────────────────────
const TourTooltip = ({ index, step, tooltipProps }) => {
  const d = step.data || {};
  const Icon = d.icon || Sparkles;
  const ac = ACCENT_MAP[d.accent] || ACCENT_MAP.indigo;
  const total = TOUR_STEPS.length;
  const progress = ((index + 1) / total) * 100;
  const isLast = index === total - 1;
  const muted = window.__d3TourMuted ?? false;
  const playing = window.__d3TourPlaying ?? false;
  const audioReady = window.__d3TourAudioReady ?? false;

  return (
    <div {...tooltipProps}>
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className={`w-[400px] rounded-2xl bg-[#0d1221] border ${ac.border} shadow-2xl ${ac.glow} overflow-hidden`}
        data-testid="product-tour-tooltip"
      >
        {/* Progress bar */}
        <div className="h-1 bg-white/5">
          <motion.div className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${ac.bg} flex items-center justify-center border ${ac.border}`}>
                <Icon className={`w-5 h-5 ${ac.text}`} />
              </div>
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-widest ${ac.text}`}>{d.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">Step {index + 1} of {total}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {audioReady && (
                <button
                  onClick={() => window.__d3TourAction?.('toggleMute')}
                  className={`p-1.5 rounded-lg transition-colors ${playing ? 'bg-indigo-500/20 text-indigo-400' : 'hover:bg-white/10 text-gray-500'}`}
                  data-testid="tour-mute-btn"
                  title={muted ? 'Unmute voiceover' : 'Mute voiceover'}
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className={`w-4 h-4 ${playing ? 'animate-pulse' : ''}`} />}
                </button>
              )}
              <button onClick={() => window.__d3TourAction?.('close')} className="p-1 rounded-lg hover:bg-white/10 transition-colors" data-testid="tour-close-btn">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <h3 className="text-white font-semibold text-base leading-snug mb-2" data-testid="tour-tooltip-title">{d.title}</h3>
          <p className="text-gray-400 text-sm leading-relaxed mb-5" data-testid="tour-tooltip-body">{d.body}</p>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button onClick={() => window.__d3TourAction?.('skip')} className="text-xs text-gray-500 hover:text-gray-300 transition-colors" data-testid="tour-skip-btn">
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {index > 0 && (
                <button onClick={() => window.__d3TourAction?.('back')} className="flex items-center gap-1 px-3 py-2 rounded-xl bg-white/5 text-gray-300 text-xs font-medium hover:bg-white/10 transition-colors" data-testid="tour-back-btn">
                  <ChevronLeft className="w-3.5 h-3.5" /> Back
                </button>
              )}
              <button onClick={() => window.__d3TourAction?.('next')} className="flex items-center gap-1.5 px-5 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white text-xs font-semibold hover:opacity-90 transition-opacity" data-testid="tour-next-btn">
                {isLast ? 'Finish' : 'Next'} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ── Welcome Modal ───────────────────────────────────────
const WelcomeModal = ({ onStart, onSkip, audioReady, muted, onToggleMute }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="tour-welcome-modal">
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onSkip} />
    <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="relative w-[460px] rounded-3xl bg-[#0d1221] border border-indigo-500/20 shadow-2xl shadow-indigo-500/10 overflow-hidden"
    >
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 via-cyan-500 to-indigo-500" />
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-indigo-500/10 to-transparent blur-3xl" />
      <div className="relative p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-5 border border-indigo-500/20">
          <Rocket className="w-8 h-8 text-indigo-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2" data-testid="tour-welcome-title">Welcome to D3 Command</h2>
        <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">See how teams manage operations, risks, and performance in one place.</p>
        <div className="mt-8 space-y-3">
          <button onClick={onStart} className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity" data-testid="tour-start-btn">
            Start 2-min Tour <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={onSkip} className="w-full px-6 py-2.5 rounded-xl bg-white/5 text-gray-400 text-sm font-medium hover:bg-white/10 hover:text-gray-300 transition-colors border border-white/10" data-testid="tour-skip-welcome-btn">
            Skip
          </button>
        </div>
        {audioReady && (
          <button
            onClick={onToggleMute}
            className="mt-4 inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
            data-testid="tour-welcome-mute-btn"
          >
            {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            {muted ? 'Voiceover off' : 'Voiceover on'}
          </button>
        )}
        <p className="mt-3 text-[11px] text-gray-600">Takes about 2 minutes</p>
      </div>
    </motion.div>
  </motion.div>
);

// ── Conversion Modal ────────────────────────────────────
const ConversionModal = ({ onSignup, onDemo, onExplore }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="tour-conversion-modal">
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onExplore} />
    <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="relative w-[480px] rounded-3xl bg-[#0d1221] border border-indigo-500/20 shadow-2xl shadow-indigo-500/10 overflow-hidden"
    >
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 via-cyan-500 to-indigo-500" />
      <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-cyan-500/10 to-transparent blur-3xl" />
      <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-tl from-indigo-500/10 to-transparent blur-3xl" />
      <div className="relative p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-5 border border-emerald-500/20">
          <Sparkles className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2" data-testid="tour-conversion-title">Ready to take control of your operations?</h2>
        <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">D3 Command gives leaders real-time visibility, accountability, and AI-driven insights — all in one platform.</p>
        <div className="mt-8 space-y-3">
          <button onClick={onSignup} className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white font-semibold hover:opacity-90 transition-opacity" data-testid="tour-create-account-btn">
            Create Free Account <ArrowRight className="w-4 h-4" />
          </button>
          <button onClick={onDemo} className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/5 text-white text-sm font-medium hover:bg-white/10 transition-colors border border-white/10" data-testid="tour-request-demo-btn">
            Request Demo
          </button>
          <button onClick={onExplore} className="text-sm text-gray-500 hover:text-gray-300 transition-colors mt-1" data-testid="tour-continue-exploring-btn">
            Continue Exploring
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// ── Main ProductTour ────────────────────────────────────
export const ProductTour = ({ onNavigate }) => {
  const { isDemo } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showConversion, setShowConversion] = useState(false);
  const controlsRef = useRef(null);
  const { play, stop, toggleMute, muted, audioReady, playing } = useTourAudio();

  // Expose audio state to tooltip via window globals (tooltip is rendered outside React tree)
  useEffect(() => {
    window.__d3TourMuted = muted;
    window.__d3TourPlaying = playing;
    window.__d3TourAudioReady = audioReady;
  }, [muted, playing, audioReady]);

  const isTourDone = () => ['completed', 'skipped'].includes(localStorage.getItem(TOUR_KEY) || '');
  const wasAutoTriggered = () => sessionStorage.getItem(TOUR_AUTO_KEY) === 'true';

  // Auto-trigger after 2s in demo mode
  useEffect(() => {
    if (!isDemo || isTourDone() || wasAutoTriggered()) return;
    const t = setTimeout(() => {
      sessionStorage.setItem(TOUR_AUTO_KEY, 'true');
      setShowWelcome(true);
    }, 2000);
    return () => clearTimeout(t);
  }, [isDemo]);

  // Play audio when step changes
  useEffect(() => {
    if (runTour) {
      play(`step_${stepIndex}`);
    }
  }, [runTour, stepIndex, play]);

  // Play conversion audio
  useEffect(() => {
    if (showConversion) {
      play('conversion');
    }
  }, [showConversion, play]);

  // Global action handler for custom tooltip buttons
  useEffect(() => {
    window.__d3TourAction = (action) => {
      if (action === 'toggleMute') {
        toggleMute();
        return;
      }
      if (action === 'next') {
        if (stepIndex >= TOUR_STEPS.length - 1) {
          stop();
          setRunTour(false);
          localStorage.setItem(TOUR_KEY, 'completed');
          setShowConversion(true);
        } else {
          setStepIndex(i => i + 1);
        }
      } else if (action === 'back') {
        setStepIndex(i => Math.max(0, i - 1));
      } else if (action === 'skip' || action === 'close') {
        stop();
        setRunTour(false);
        localStorage.setItem(TOUR_KEY, 'skipped');
      }
    };
    return () => { delete window.__d3TourAction; };
  }, [stepIndex, stop, toggleMute]);

  // Global restart function for Tour button
  useEffect(() => {
    window.__d3RestartTour = () => {
      setStepIndex(0);
      setShowConversion(false);
      setShowWelcome(true);
      if (onNavigate) onNavigate('dashboard');
    };
    return () => { delete window.__d3RestartTour; };
  }, [onNavigate]);

  const startTour = () => {
    setShowWelcome(false);
    setStepIndex(0);
    if (onNavigate) onNavigate('dashboard');
    play('welcome');
    // Start actual tour steps after welcome audio has a moment to begin
    setTimeout(() => setRunTour(true), 300);
  };

  const skipTour = () => {
    stop();
    setShowWelcome(false);
    localStorage.setItem(TOUR_KEY, 'skipped');
  };

  // Pulse effect for risk card step
  useEffect(() => {
    if (!runTour) return;
    const sd = TOUR_STEPS[stepIndex]?.data;
    if (sd?.pulse) {
      const el = document.querySelector(TOUR_STEPS[stepIndex].target);
      if (el) {
        el.classList.add('tour-pulse-glow');
        return () => el.classList.remove('tour-pulse-glow');
      }
    }
  }, [runTour, stepIndex]);

  const handleCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED) {
      setRunTour(false);
      localStorage.setItem(TOUR_KEY, 'completed');
      setShowConversion(true);
    } else if (status === STATUS.SKIPPED) {
      setRunTour(false);
      localStorage.setItem(TOUR_KEY, 'skipped');
    }
  };

  return (
    <>
      <style>{`
        .tour-pulse-glow { animation: tourPulse 2s ease-in-out infinite; }
        @keyframes tourPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.3); }
          50% { box-shadow: 0 0 20px 4px rgba(239, 68, 68, 0.15); }
        }
        .__floater { z-index: 150 !important; }
      `}</style>

      <AnimatePresence>{showWelcome && <WelcomeModal onStart={startTour} onSkip={skipTour} audioReady={audioReady} muted={muted} onToggleMute={toggleMute} />}</AnimatePresence>

      {runTour && (
        <Joyride
          steps={TOUR_STEPS}
          run={runTour}
          stepIndex={stepIndex}
          continuous
          scrollToFirstStep
          tooltipComponent={TourTooltip}
          callback={handleCallback}
          options={{
            zIndex: 150,
            overlayColor: 'rgba(0, 0, 0, 0.75)',
            spotlightPadding: 12,
            spotlightRadius: 16,
            arrowColor: '#0d1221',
            skipBeacon: true,
          }}
        />
      )}

      <AnimatePresence>
        {showConversion && (
          <ConversionModal
            onSignup={() => { stop(); setShowConversion(false); window.history.pushState({}, '', '/signup'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            onDemo={() => { stop(); setShowConversion(false); window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            onExplore={() => { stop(); setShowConversion(false); }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductTour;
